import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { login, verify2fa, setup2fa, isAuthenticated } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { loadRecaptchaScript, getRecaptchaToken, getSiteKey } from "@/lib/recaptcha";
import { Loader2, ShieldCheck, Copy, ExternalLink } from "lucide-react";
import { getHomePath } from "@/lib/homePath";

/** Normaliza la respuesta de sesión del backend al objeto de usuario del AuthContext. */
function userFromSession(data) {
  const perms = data.permissions || {};
  return {
    email: data.email,
    nombre: data.nombre ?? data.email?.split("@")[0] ?? "Usuario",
    color: data.color ?? "#3b82f6",
    avatar_variant: data.avatar_variant ?? "marble",
    is_admin: data.is_admin === true,
    permissions: {
      candidatos: perms.candidatos !== false,
      formularios: perms.formularios !== false,
      revisiones: perms.revisiones !== false && perms.formularios !== false,
      proveedores: perms.proveedores !== false,
      archivos: perms.archivos !== false,
      metricas: perms.metricas === true,
      correos: perms.correos !== false,
      onboarding: perms.onboarding !== false,
      gmb: perms.gmb !== false,
      material: perms.material !== false,
      admin: perms.admin === true,
    },
  };
}

/**
 * Login con 2FA obligatorio (Google Authenticator).
 * Pasos: password → code (ya tiene 2FA) | setup (primera vez: QR) → recovery (códigos de recuperación) → dentro.
 * Con «Recordar este dispositivo» el backend devuelve un device_token y durante 30 días basta la contraseña.
 */
export default function LoginPage() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [step, setStep] = useState("password"); // password | code | setup | recovery
  const [mfa, setMfa] = useState(null); // { token, qrSvg, otpauthUri, secret }
  const [code, setCode] = useState("");
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(true);
  const [pendingSession, setPendingSession] = useState(null); // sesión ya válida, a la espera de que guarde los códigos
  const mounted = useRef(true);

  useEffect(() => {
    if (!getSiteKey()) {
      setRecaptchaReady(true);
      return;
    }
    loadRecaptchaScript().then(() => {
      if (mounted.current) setRecaptchaReady(true);
    });
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!isAuthenticated() || !user) return;
    const home = getHomePath(user.permissions);
    if (home) navigate(home);
  }, [navigate, user]);

  const completeLogin = (data) => {
    const nextUser = userFromSession(data);
    setUser(nextUser);
    const home = getHomePath(nextUser.permissions);
    if (home) {
      toast.success("Sesión iniciada");
      navigate(home);
    } else {
      toast.error("Tu usuario no tiene acceso a ninguna sección. Pide permisos a un administrador.");
    }
  };

  const backToPassword = () => {
    setStep("password");
    setMfa(null);
    setCode("");
    setUseRecoveryCode(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim() || !password) {
      toast.error("Por favor, completa todos los campos");
      return;
    }

    let recaptchaToken = null;
    if (getSiteKey()) {
      try {
        recaptchaToken = await getRecaptchaToken("login");
      } catch (err) {
        console.error(err);
        toast.error("Error al verificar el captcha. Recarga la página e inténtalo de nuevo.");
        return;
      }
    }

    setIsLoading(true);

    try {
      const data = await login(email.trim(), password, recaptchaToken);
      if (data.token) {
        completeLogin(data);
      } else if (data.mfa_setup_required) {
        setMfa({ token: data.mfa_token, qrSvg: data.qr_svg, otpauthUri: data.otpauth_uri, secret: data.secret });
        setCode("");
        setStep("setup");
      } else if (data.mfa_required) {
        setMfa({ token: data.mfa_token });
        setCode("");
        setStep("code");
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Credenciales inválidas");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCode = async (e) => {
    e.preventDefault();
    if (!code.trim() || !mfa?.token) return;
    setIsLoading(true);
    try {
      if (step === "setup") {
        const data = await setup2fa(mfa.token, code.trim(), rememberDevice);
        setPendingSession(data);
        setStep("recovery");
      } else {
        const data = await verify2fa(mfa.token, code.trim(), rememberDevice);
        completeLogin(data);
      }
    } catch (error) {
      console.error(error);
      setCode("");
      toast.error(error.response?.data?.detail || "Código incorrecto");
      // El paso caducó o ya no es válido: volver a la contraseña
      if (error.response?.status === 400 || (error.response?.status === 401 && /sesión/i.test(error.response?.data?.detail || ""))) {
        backToPassword();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const copyText = async (text, ok) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(ok);
    } catch (_) {
      toast.error("No se pudo copiar");
    }
  };

  const recoveryCodes = pendingSession?.recovery_codes || [];

  const codeInput = (
    <div className="space-y-2">
      <Label htmlFor="mfa-code" className="text-sm font-medium">
        {useRecoveryCode ? "Código de recuperación" : "Código de 6 dígitos"}
      </Label>
      <Input
        id="mfa-code"
        value={code}
        onChange={(e) => setCode(useRecoveryCode ? e.target.value : e.target.value.replace(/\D/g, "").slice(0, 6))}
        disabled={isLoading}
        inputMode={useRecoveryCode ? "text" : "numeric"}
        autoComplete="one-time-code"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        placeholder={useRecoveryCode ? "xxxx-xxxx-xxxx" : "123456"}
        className="h-11 text-center tracking-[0.3em] font-mono text-lg"
        autoFocus
        data-testid="mfa-code-input"
      />
    </div>
  );

  const rememberCheckbox = (
    <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
      <Checkbox checked={rememberDevice} onCheckedChange={(v) => setRememberDevice(v === true)} />
      Recordar este dispositivo 30 días
    </label>
  );

  return (
    <div className="min-h-dvh flex items-center justify-center p-6 bg-fondo-candidatos">
      <div className="w-full max-w-sm animate-fade-in">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-2xl font-semibold tracking-tight mb-2" data-testid="login-title">
            {step === "password" ? "Acceso" : step === "recovery" ? "Códigos de recuperación" : "Verificación en dos pasos"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {step === "password" && "Panel de administración"}
            {step === "code" && "Abre Google Authenticator e introduce el código de Brutalco."}
            {step === "setup" && "Es obligatorio activarla para entrar. Solo tienes que hacerlo una vez."}
            {step === "recovery" && "Guárdalos en un lugar seguro. Cada uno sirve una vez si pierdes el móvil."}
          </p>
        </div>

        {step === "password" && (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                className="h-11"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">
                Contraseña
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                className="h-11"
                data-testid="password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-11 text-sm font-medium"
              disabled={isLoading || !recaptchaReady}
              data-testid="login-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Iniciando sesión...
                </>
              ) : (
                "Iniciar sesión"
              )}
            </Button>
          </form>
        )}

        {step === "code" && (
          <form onSubmit={handleCode} className="space-y-5">
            {codeInput}
            {rememberCheckbox}
            <Button type="submit" className="w-full h-11 text-sm font-medium" disabled={isLoading || !code.trim()} data-testid="mfa-verify-btn">
              {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Verificar
            </Button>
            <button
              type="button"
              onClick={() => { setUseRecoveryCode((v) => !v); setCode(""); }}
              className="block w-full text-center text-xs text-muted-foreground hover:text-foreground"
            >
              {useRecoveryCode ? "Usar el código de Google Authenticator" : "¿Has perdido el móvil? Usa un código de recuperación"}
            </button>
          </form>
        )}

        {step === "setup" && mfa && (
          <form onSubmit={handleCode} className="space-y-5">
            <ol className="space-y-4 text-sm">
              <li>
                <span className="font-medium">1. Instala Google Authenticator</span> en tu móvil (App Store o Google Play).
              </li>
              <li className="space-y-3">
                <span className="font-medium">2. Añade la cuenta de Brutalco:</span>
                <span className="block text-muted-foreground">Desde el ordenador, escanea el QR con la app. Si estás en el móvil, pulsa «Abrir en la app».</span>
                {mfa.qrSvg && (
                  <img src={mfa.qrSvg} alt="Código QR para Google Authenticator" className="mx-auto h-48 w-48 rounded-lg border bg-white p-2" />
                )}
                <a
                  href={mfa.otpauthUri}
                  className="flex h-11 w-full items-center justify-center gap-2 rounded-md border border-input bg-background text-sm font-medium md:hidden"
                >
                  <ExternalLink className="w-4 h-4" /> Abrir en la app
                </a>
                <div className="rounded-md bg-muted/60 px-3 py-2">
                  <p className="text-xs text-muted-foreground mb-1">O introduce esta clave a mano:</p>
                  <div className="flex items-center justify-between gap-2">
                    <code className="font-mono text-xs break-all">{mfa.secret}</code>
                    <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => copyText(mfa.secret, "Clave copiada")} aria-label="Copiar clave">
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </li>
              <li className="font-medium">3. Escribe el código que muestra la app:</li>
            </ol>
            {codeInput}
            {rememberCheckbox}
            <Button type="submit" className="w-full h-11 text-sm font-medium" disabled={isLoading || code.length !== 6} data-testid="mfa-setup-btn">
              {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Activar y entrar
            </Button>
          </form>
        )}

        {step === "recovery" && pendingSession && (
          <div className="space-y-5">
            <ul className="grid grid-cols-2 gap-2 rounded-lg border bg-muted/40 p-3 font-mono text-sm">
              {recoveryCodes.map((c) => (
                <li key={c} className="text-center">{c}</li>
              ))}
            </ul>
            <Button type="button" variant="outline" className="w-full h-11 gap-2" onClick={() => copyText(recoveryCodes.join("\n"), "Códigos copiados")}>
              <Copy className="w-4 h-4" /> Copiar códigos
            </Button>
            <Button type="button" className="w-full h-11 text-sm font-medium" onClick={() => completeLogin(pendingSession)} data-testid="mfa-recovery-continue">
              Los he guardado, continuar
            </Button>
          </div>
        )}

        {/* Back link */}
        <div className="mt-8 text-center">
          {step === "password" ? (
            <button
              onClick={() => navigate("/")}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              data-testid="back-link"
            >
              ← Volver al inicio
            </button>
          ) : step !== "recovery" ? (
            <button
              type="button"
              onClick={backToPassword}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              ← Volver
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
